// A tiny router on top of the History API.
//
// Android's back button must close menus and sheets instead of leaving the
// app (design 7.3), so every open sheet pushes a history entry
// (useBackToClose) and the back button pops it.
import { useEffect, useRef, useSyncExternalStore } from 'react'

const listeners = new Set<() => void>()

function emit(): void {
  for (const l of listeners) l()
}

if (typeof window !== 'undefined') window.addEventListener('popstate', emit)

function subscribe(l: () => void): () => void {
  listeners.add(l)
  return () => listeners.delete(l)
}

export function usePath(): string {
  return useSyncExternalStore(subscribe, () => location.pathname)
}

/** One query parameter of the address ("?card=12" from a notification). */
export function useSearchParam(name: string): string | null {
  const search = useSyncExternalStore(subscribe, () => location.search)
  return new URLSearchParams(search).get(name)
}

interface NavState {
  depth?: number
  sheet?: string
}

function navState(): NavState {
  return (history.state as NavState | null) ?? {}
}

export function navigate(to: string, opts: { replace?: boolean } = {}): void {
  // a closed layer's history.back() is still on its way (leaving a room from
  // its menu): move once it has landed, or it would take this move back
  if (pendingBacks > 0) {
    afterBacks.push(() => navigate(to, opts))
    return
  }
  const depth = navState().depth ?? 0
  if (opts.replace) history.replaceState({ depth }, '', to)
  else history.pushState({ depth: depth + 1 }, '', to)
  emit()
}

/** Leaves for another page from an open sheet: the sheet's history entry
    becomes the page, so the back button returns to where the sheet was
    opened, and the sheet's own late history.back() cannot undo the move. */
export function navigateFromSheet(to: string): void {
  const s = navState()
  if (s.sheet === undefined) return navigate(to)
  history.replaceState({ depth: (s.depth ?? 0) + 1 }, '', to)
  emit()
}

/** Changes the address's query in place (no new history entry). */
export function replaceQuery(params: Record<string, string | null>): void {
  const url = new URL(location.href)
  for (const [k, v] of Object.entries(params)) {
    if (v === null || v === '') url.searchParams.delete(k)
    else url.searchParams.set(k, v)
  }
  if (url.href === location.href) return
  history.replaceState(history.state, '', url.pathname + url.search)
  emit()
}

/** Back inside the app; when the page was opened directly, go to fallback. */
export function goBack(fallback = '/'): void {
  if ((navState().depth ?? 0) > 0) history.back()
  else navigate(fallback, { replace: true })
}

// Open layers (sheets, the viewer, the chat drawer), bottom first. Each
// has its own history entry; the back button closes the top one only.
// A layer closed by the app takes its entry back with history.back(),
// which finishes later (popstate): a layer opened meanwhile adds its entry
// only after that, or the late "back" would close it.
interface Layer {
  marker: string
  close: () => void
  pushed: boolean
}

const layers: Layer[] = []
let pendingBacks = 0
let afterBacks: (() => void)[] = []

function pushLayer(l: Layer): void {
  history.pushState({ ...navState(), sheet: l.marker }, '')
  l.pushed = true
}

if (typeof window !== 'undefined') {
  window.addEventListener('popstate', () => {
    if (pendingBacks > 0) {
      pendingBacks--
      if (pendingBacks === 0) {
        const moves = afterBacks
        afterBacks = []
        for (const move of moves) move()
        for (const l of layers) if (!l.pushed) pushLayer(l)
      }
      return
    }
    const top = layers[layers.length - 1]
    if (top && top.pushed && navState().sheet !== top.marker) {
      layers.pop()
      top.close()
    }
  })
}

/** While `open`, the back button calls close() instead of navigating. */
export function useBackToClose(open: boolean, close: () => void): void {
  const closeRef = useRef(close)
  closeRef.current = close
  useEffect(() => {
    if (!open) return
    const layer: Layer = {
      marker: Math.random().toString(36).slice(2),
      close: () => closeRef.current(),
      pushed: false,
    }
    layers.push(layer)
    if (pendingBacks === 0) pushLayer(layer)
    return () => {
      const i = layers.indexOf(layer)
      if (i < 0) return // closed by the back button
      layers.splice(i, 1)
      if (layer.pushed && navState().sheet === layer.marker) {
        pendingBacks++
        history.back()
      }
    }
  }, [open])
}

export function useMedia(query: string): boolean {
  return useSyncExternalStore(
    (l) => {
      const m = matchMedia(query)
      m.addEventListener('change', l)
      return () => m.removeEventListener('change', l)
    },
    () => matchMedia(query).matches,
  )
}
